// ============================================
// Path: /frontend/src/pages/MySubmissionPage.jsx
// Purpose: User views their submissions with role filter
// ============================================

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { jointVenturesAPI } from '../api/jointVentures.api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Loader2, AlertCircle, CheckCircle2, Clock, XCircle, FileText, MapPin, Calendar, DollarSign, Ruler, Building, ChevronLeft, ChevronRight } from 'lucide-react';
import { Alert, AlertDescription } from '../components/ui/alert';

const STATUS_CONFIG = {
  pending: {
    label: 'Pending Review',
    variant: 'secondary',
    icon: Clock,
    description: 'Your submission is being reviewed by our team.'
  },
  active: {
    label: 'Active',
    variant: 'default',
    icon: CheckCircle2,
    description: 'Your submission is active. We\'ll notify you when we find a match.'
  },
  matched: {
    label: 'Matched',
    variant: 'default',
    icon: CheckCircle2,
    description: 'Great news! We found a potential match. Our team is reviewing it.'
  },
  connected: {
    label: 'Connected',
    variant: 'default',
    icon: CheckCircle2,
    description: 'You\'ve been connected with a partner. Check your email for details.'
  },
  rejected: {
    label: 'Rejected',
    variant: 'destructive',
    icon: XCircle,
    description: 'Unfortunately, your submission did not meet our criteria.'
  }
};

export default function MySubmissionPage() {
  console.log('🎯 [MySubmissionPage] Component rendered');

  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [submissions, setSubmissions] = useState([]);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [roleFilter, setRoleFilter] = useState(null); // null = All, 'LAND_OWNER', 'INVESTOR'
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, totalPages: 0 });

  // ========================================
  // FETCH SUBMISSIONS
  // ========================================
  const fetchSubmissions = async (role = null, page = 1) => {
    if (!user) return;

    console.log('📡 [MySubmissionPage] Fetching submissions:', { role, page });
    setLoading(true);
    
    try {
      const response = await jointVenturesAPI.getMySubmission(role, page, 10);
      console.log('✅ [MySubmissionPage] Submissions fetched:', response);

      setSubmissions(response.submissions || []);
      setPagination(response.pagination || { page: 1, limit: 10, total: 0, totalPages: 0 });
      
      // If viewing detail and submissions change, clear selection
      if (selectedSubmission) {
        const stillExists = response.submissions?.find(s => s.id === selectedSubmission.id);
        if (!stillExists) setSelectedSubmission(null);
      }
    } catch (err) {
      console.error('❌ [MySubmissionPage] Error:', err);
      setError(err.response?.data?.message || 'Failed to load your submissions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!authLoading) {
      fetchSubmissions(roleFilter, pagination.page);
    }
  }, [user, authLoading, roleFilter, pagination.page]);

  // ========================================
  // HANDLE ROLE FILTER CHANGE
  // ========================================
  const handleRoleFilterChange = (role) => {
    setRoleFilter(role);
    setPagination(prev => ({ ...prev, page: 1 }));
    setSelectedSubmission(null);
  };

  // ========================================
  // HANDLE PAGE CHANGE
  // ========================================
  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
    setSelectedSubmission(null);
  };

  // ========================================
  // LOADING STATE
  // ========================================
  if (authLoading || (loading && submissions.length === 0)) {
    return (
      <div className="container py-8 flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  // ========================================
  // NOT AUTHENTICATED
  // ========================================
  if (!user) {
    return (
      <div className="container py-8">
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Please <a href="/login" className="underline">login</a> to view your submissions.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  // ========================================
  // ERROR STATE
  // ========================================
  if (error) {
    return (
      <div className="container py-8">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      </div>
    );
  }

  // ========================================
  // RENDER DETAIL VIEW
  // ========================================
  if (selectedSubmission) {
    const statusInfo = STATUS_CONFIG[selectedSubmission.status] || STATUS_CONFIG.pending;
    const StatusIcon = statusInfo.icon;
    const isLandowner = selectedSubmission.role === 'LAND_OWNER';
    const details = selectedSubmission;

    return (
      <div className="container py-8 max-w-4xl">
        <div className="space-y-6">
          {/* BACK BUTTON */}
          <Button variant="outline" onClick={() => setSelectedSubmission(null)}>
            ← Back to List
          </Button>

          {/* STATUS CARD */}
          <Card>
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <StatusIcon className="h-6 w-6" />
                  <div>
                    <CardTitle>Status: {statusInfo.label}</CardTitle>
                    <CardDescription className="mt-1">{statusInfo.description}</CardDescription>
                  </div>
                </div>
                <Badge variant={statusInfo.variant}>{statusInfo.label}</Badge>
              </div>
            </CardHeader>
          </Card>

          {/* SUBMISSION DETAILS */}
          <Card>
            <CardHeader>
              <CardTitle>Submission Details</CardTitle>
              <CardDescription>
                Submitted as: <span className="font-medium">{isLandowner ? 'Land Owner' : 'Investor'}</span>
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* PERSONAL INFO */}
              <div>
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Personal Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Name</p>
                    <p className="font-medium">{details.name || user.fullName}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Email</p>
                    <p className="font-medium">{selectedSubmission.userEmail}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Phone</p>
                    <p className="font-medium">{selectedSubmission.phone || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Submitted</p>
                    <p className="font-medium">{new Date(selectedSubmission.createdAt).toLocaleDateString()}</p>
                  </div>
                </div>
              </div>

              <Separator />

              {/* LOCATION */}
              {details.county && (
                <>
                  <div>
                    <h3 className="font-semibold mb-3 flex items-center gap-2">
                      <MapPin className="h-4 w-4" />
                      Location
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-muted-foreground">County</p>
                        <p className="font-medium">{details.county}</p>
                      </div>
                      {details.subCounty && (
                        <div>
                          <p className="text-sm text-muted-foreground">Sub-County</p>
                          <p className="font-medium">{details.subCounty}</p>
                        </div>
                      )}
                    </div>
                  </div>
                  <Separator />
                </>
              )}

              {/* TIMELINE */}
              {details.timeline && (
                <>
                  <div>
                    <h3 className="font-semibold mb-3 flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      Timeline
                    </h3>
                    <div>
                      <p className="text-sm text-muted-foreground">Project Timeline</p>
                      <p className="font-medium">{details.timeline}</p>
                    </div>
                  </div>
                  <Separator />
                </>
              )}

              {/* LANDOWNER SPECIFIC */}
              {isLandowner && (
                <>
                  <div>
                    <h3 className="font-semibold mb-3 flex items-center gap-2">
                      <Ruler className="h-4 w-4" />
                      Land Details
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {details.landSize && (
                        <div>
                          <p className="text-sm text-muted-foreground">Land Size</p>
                          <p className="font-medium">{details.landSize} Acres</p>
                        </div>
                      )}
                      {details.titleDeedStatus && (
                        <div>
                          <p className="text-sm text-muted-foreground">Title Deed Status</p>
                          <p className="font-medium capitalize">{details.titleDeedStatus}</p>
                        </div>
                      )}
                      {details.developmentType && (
                        <div>
                          <p className="text-sm text-muted-foreground">Development Type</p>
                          <p className="font-medium">{details.developmentType}</p>
                        </div>
                      )}
                      {details.landValue && (
                        <div>
                          <p className="text-sm text-muted-foreground">Land Value</p>
                          <p className="font-medium">KES {details.landValue.toLocaleString()}</p>
                        </div>
                      )}
                      {details.capitalNeeded && (
                        <div>
                          <p className="text-sm text-muted-foreground">Capital Needed</p>
                          <p className="font-medium">KES {details.capitalNeeded.toLocaleString()}</p>
                        </div>
                      )}
                    </div>
                    {details.landDetails && (
                      <div className="mt-4">
                        <p className="text-sm text-muted-foreground">Additional Details</p>
                        <p className="font-medium">{details.landDetails}</p>
                      </div>
                    )}
                  </div>
                </>
              )}

              {/* INVESTOR SPECIFIC */}
              {!isLandowner && (
                <>
                  <div>
                    <h3 className="font-semibold mb-3 flex items-center gap-2">
                      <DollarSign className="h-4 w-4" />
                      Investment Details
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {details.investmentAmount && (
                        <div>
                          <p className="text-sm text-muted-foreground">Investment Amount</p>
                          <p className="font-medium">KES {details.investmentAmount.toLocaleString()}</p>
                        </div>
                      )}
                      {details.experienceLevel && (
                        <div>
                          <p className="text-sm text-muted-foreground">Experience Level</p>
                          <p className="font-medium capitalize">{details.experienceLevel.replace('_', ' ')}</p>
                        </div>
                      )}
                      {details.investmentMin && (
                        <div>
                          <p className="text-sm text-muted-foreground">Min Investment</p>
                          <p className="font-medium">KES {details.investmentMin.toLocaleString()}</p>
                        </div>
                      )}
                      {details.investmentMax && (
                        <div>
                          <p className="text-sm text-muted-foreground">Max Investment</p>
                          <p className="font-medium">KES {details.investmentMax.toLocaleString()}</p>
                        </div>
                      )}
                      {details.projectTypeInterest && (
                        <div>
                          <p className="text-sm text-muted-foreground">Project Interest</p>
                          <p className="font-medium">{details.projectTypeInterest}</p>
                        </div>
                      )}
                      {details.landSizeMin && details.landSizeMax && (
                        <div>
                          <p className="text-sm text-muted-foreground">Land Size Range</p>
                          <p className="font-medium">{details.landSizeMin} - {details.landSizeMax} Acres</p>
                        </div>
                      )}
                    </div>
                    {details.investorNotes && (
                      <div className="mt-4">
                        <p className="text-sm text-muted-foreground">Additional Notes</p>
                        <p className="font-medium">{details.investorNotes}</p>
                      </div>
                    )}
                  </div>
                </>
              )}

              {/* ADMIN NOTES */}
              {details.adminNotes && (
                <>
                  <Separator />
                  <div>
                    <h3 className="font-semibold mb-3 flex items-center gap-2">
                      <Building className="h-4 w-4" />
                      Admin Notes
                    </h3>
                    <Alert>
                      <AlertDescription>{details.adminNotes}</AlertDescription>
                    </Alert>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* NEXT STEPS */}
          <Card>
            <CardHeader>
              <CardTitle>What Happens Next?</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 text-sm">
                <div className="flex gap-3">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center">
                    <span className="text-xs font-bold">1</span>
                  </div>
                  <div>
                    <p className="font-medium">Review</p>
                    <p className="text-muted-foreground">Our team reviews your submission for completeness and accuracy.</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center">
                    <span className="text-xs font-bold">2</span>
                  </div>
                  <div>
                    <p className="font-medium">Matching</p>
                    <p className="text-muted-foreground">Our algorithm searches for potential matches based on your criteria.</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center">
                    <span className="text-xs font-bold">3</span>
                  </div>
                  <div>
                    <p className="font-medium">Vetting</p>
                    <p className="text-muted-foreground">If a match is found (90%+ score), our admin will contact both parties for verification.</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center">
                    <span className="text-xs font-bold">4</span>
                  </div>
                  <div>
                    <p className="font-medium">Connection</p>
                    <p className="text-muted-foreground">After vetting, we'll send introduction emails with contact details to both parties.</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* DISCLAIMER */}
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              <strong>Important:</strong> We are a matching service only. Always verify identity, land ownership, and use legal contracts before proceeding with any deal.
            </AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  // ========================================
  // RENDER LIST VIEW
  // ========================================
  return (
    <div className="container py-8 max-w-6xl">
      <div className="space-y-6">
        {/* HEADER */}
        <div className="space-y-4">
          <Button variant="ghost" onClick={() => navigate('/dashboard')}>
            ← Back to Dashboard
          </Button>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold">My Submissions</h1>
              <p className="text-muted-foreground">Track all your joint venture interests</p>
            </div>
            <Button onClick={() => navigate('/joint-ventures')}>
              Submit New
            </Button>
          </div>
        </div>

        {/* ROLE FILTER TOGGLE */}
        <Card>
          <CardHeader>
            <CardTitle>Filter by Role</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-2">
              <Button
                variant={roleFilter === null ? 'default' : 'outline'}
                onClick={() => handleRoleFilterChange(null)}
              >
                All ({pagination.total})
              </Button>
              <Button
                variant={roleFilter === 'LAND_OWNER' ? 'default' : 'outline'}
                onClick={() => handleRoleFilterChange('LAND_OWNER')}
              >
                Landowner
              </Button>
              <Button
                variant={roleFilter === 'INVESTOR' ? 'default' : 'outline'}
                onClick={() => handleRoleFilterChange('INVESTOR')}
              >
                Investor
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* NO SUBMISSIONS */}
        {submissions.length === 0 && !loading && (
          <Card>
            <CardHeader>
              <CardTitle>No Submissions Yet</CardTitle>
              <CardDescription>
                {roleFilter 
                  ? `You haven't submitted any ${roleFilter === 'LAND_OWNER' ? 'Landowner' : 'Investor'} interests yet.`
                  : "You haven't submitted a joint venture interest yet."
                }
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button onClick={() => navigate('/joint-ventures')}>
                Submit Interest
              </Button>
            </CardContent>
          </Card>
        )}

        {/* SUBMISSIONS LIST */}
        {submissions.length > 0 && (
          <>
            <div className="grid gap-4">
              {submissions.map((submission) => {
                const statusInfo = STATUS_CONFIG[submission.status] || STATUS_CONFIG.pending;
                const StatusIcon = statusInfo.icon;
                const isLandowner = submission.role === 'LAND_OWNER';

                return (
                  <Card key={submission.id} className="hover:shadow-md transition-shadow">
                    <CardContent className="pt-6">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 space-y-3">
                          {/* HEADER */}
                          <div className="flex items-center gap-3">
                            <Badge variant={isLandowner ? 'default' : 'secondary'}>
                              {isLandowner ? 'Landowner' : 'Investor'}
                            </Badge>
                            <Badge variant={statusInfo.variant}>
                              <StatusIcon className="h-3 w-3 mr-1" />
                              {statusInfo.label}
                            </Badge>
                          </div>

                          {/* DETAILS GRID */}
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                            <div>
                              <p className="text-muted-foreground">Location</p>
                              <p className="font-medium">{submission.county || 'N/A'}</p>
                            </div>
                            <div>
                              <p className="text-muted-foreground">Submitted</p>
                              <p className="font-medium">{new Date(submission.createdAt).toLocaleDateString()}</p>
                            </div>
                            {isLandowner ? (
                              <>
                                <div>
                                  <p className="text-muted-foreground">Land Size</p>
                                  <p className="font-medium">{submission.landSize ? `${submission.landSize} Acres` : 'N/A'}</p>
                                </div>
                                <div>
                                  <p className="text-muted-foreground">Capital Needed</p>
                                  <p className="font-medium">{submission.capitalNeeded ? `KES ${submission.capitalNeeded.toLocaleString()}` : 'N/A'}</p>
                                </div>
                              </>
                            ) : (
                              <>
                                <div>
                                  <p className="text-muted-foreground">Investment</p>
                                  <p className="font-medium">{submission.investmentAmount ? `KES ${submission.investmentAmount.toLocaleString()}` : 'N/A'}</p>
                                </div>
                                <div>
                                  <p className="text-muted-foreground">Experience</p>
                                  <p className="font-medium capitalize">{submission.experienceLevel?.replace('_', ' ') || 'N/A'}</p>
                                </div>
                              </>
                            )}
                          </div>
                        </div>

                        {/* VIEW BUTTON */}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setSelectedSubmission(submission)}
                          className="ml-4"
                        >
                          View Details
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* PAGINATION */}
            {pagination.totalPages > 1 && (
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-muted-foreground">
                      Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} submissions
                    </p>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(pagination.page - 1)}
                        disabled={pagination.page === 1}
                      >
                        <ChevronLeft className="h-4 w-4" />
                        Previous
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(pagination.page + 1)}
                        disabled={pagination.page === pagination.totalPages}
                      >
                        Next
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>
    </div>
  );
}